import React, { useState, useEffect } from 'react';
import { getCurrentWeather, getDailyForecast, WeatherAPIError, getCurrentLocation, Location, WeatherData } from '../services/weatherService';
import { useTheme } from '../hooks/useTheme';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import locationSimulationService from '../services/locationSimulationService';
import WeatherDebugService from '../services/weatherDebugService';
import MoonPhaseService from '../services/moonPhaseService';
import OptionsModal from './OptionsModal';
import APIErrorModal from './APIErrorModal';
import AirQualityModal from './AirQualityModal';
import UVIndexModal from './UVIndexModal';
import './WeatherWidget.css';

const __DEV__ = process.env.NODE_ENV !== 'production';

interface ForecastHour {
  time: string;
  temperature: number;
  description: string;
  icon: string;
  humidity: number;
  windSpeed: number;
  hasRain?: boolean;
  pop?: number;
  // Enhanced weather details
  pressure?: number;
  dewPoint?: number;
  visibility?: number;
  windDirection?: number;
  cloudCover?: number;
}

interface DailyForecast {
  date: string;
  temperature: number;
  tempMin: number;
  tempMax: number;
  description: string;
  icon: string;
  humidity: number;
  windSpeed: number;
  hasRain?: boolean;
  pop?: number;
  // Enhanced weather details
  pressure?: number;
  dewPoint?: number;
  windDirection?: number;
  cloudCover?: number;
  // Daily specific data
  sunrise: string;
  sunset: string;
  uvIndex: number;
}

const WeatherWidget: React.FC = () => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<WeatherAPIError | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [temperatureUnit, setTemperatureUnit] = useState<'c' | 'f' | 'k'>('c');
  const [distanceUnit, setDistanceUnit] = useState<'m' | 'i'>('m');
  const [showWeatherTooltip, setShowWeatherTooltip] = useState<boolean>(false);
  const [showForecast, setShowForecast] = useState<boolean>(false);
  const [forecast, setForecast] = useState<ForecastHour[]>([]);
  const [dailyForecast, setDailyForecast] = useState<DailyForecast[]>([]);
  const [forecastLoading, setForecastLoading] = useState<boolean>(false);
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [expandedAlerts, setExpandedAlerts] = useState<Set<number>>(new Set());
  const [moonPhase, setMoonPhase] = useState<any>(null);
  const [isAirQualityOpen, setIsAirQualityOpen] = useState(false);
  const [isUVIndexOpen, setIsUVIndexOpen] = useState(false);
  const [currentForecastPage, setCurrentForecastPage] = useState<number>(0);
  const [forecastMode, setForecastMode] = useState<'hourly' | 'daily'>('hourly');
  const [isMaximized, setIsMaximized] = useState<boolean>(() => {
    try {
      return localStorage.getItem('weatherMaximized') === 'true';
    } catch {
      return false;
    }
  });
  
  const { theme } = useTheme();
  const ldClient = useLDClient();
  const { getShowMoonPhase } = useFeatureFlags();
  const weatherDebug = WeatherDebugService.getInstance();
  const moonPhaseService = MoonPhaseService.getInstance();

  // Get LaunchDarkly flags - Following Rule 1: Centralized evaluation with predictable fallback
  const enableAnimations = ldClient ? ldClient.variation('enable-animations', true) : true;
  const showExtraWeatherInfo = ldClient ? ldClient.variation('show-extra-weather-info', true) : true;
  const enableMaximizedLayout = ldClient ? ldClient.variation('enable-maximized-layout', false) : false;
  const showMaximized = enableMaximizedLayout && isMaximized;
  const forecastItemsPerPage = showMaximized ? 6 : 4;

  const trackMaximizedLayoutError = (errorMsg: string) => {
    try {
      ldClient?.track('enable-maximized-layout-error', { error: errorMsg, timestamp: new Date().toISOString() });
    } catch {
      // Never let telemetry errors break the interaction
    }
  };

  const setMaximizedPreference = (maximized: boolean) => {
    setIsMaximized(maximized);
    setCurrentForecastPage(0);
    try {
      localStorage.setItem('weatherMaximized', maximized ? 'true' : 'false');
    } catch (storageError) {
      // Ignore storage errors (private browsing, etc.)
      trackMaximizedLayoutError(storageError instanceof Error ? storageError.message : 'localStorage write failed');
    }
  };

  const handleMaximize = () => {
    if (enableMaximizedLayout) {
      setMaximizedPreference(true);
      try {
        ldClient?.track('enable-maximized-layout-maximize', { action: 'maximize', timestamp: new Date().toISOString() });
      } catch {
        // Never let telemetry errors break the interaction
      }
    }
  };

  const handleRestore = () => {
    if (enableMaximizedLayout) {
      setMaximizedPreference(false);
      try {
        ldClient?.track('enable-maximized-layout-restore', { action: 'restore', timestamp: new Date().toISOString() });
      } catch {
        // Never let telemetry errors break the interaction
      }
    }
  };

  // Calculate forecast pagination for hourly forecast
  const getForecastPage = (forecastData: ForecastHour[], page: number) => {
    const startIndex = page * forecastItemsPerPage;
    return forecastData.slice(startIndex, startIndex + forecastItemsPerPage);
  };

  // Calculate daily forecast pagination
  const getDailyForecastPage = (dailyData: any[], page: number) => {
    const startIndex = page * forecastItemsPerPage;
    return dailyData.slice(startIndex, startIndex + forecastItemsPerPage);
  };

  const totalForecastPages = forecastMode === 'hourly' 
    ? Math.ceil(forecast.length / forecastItemsPerPage)
    : Math.ceil(dailyForecast.length / forecastItemsPerPage);

  const fetchWeather = async () => {
    try {
      __DEV__ && console.log('🔍 DEBUG: fetchWeather called');
      setLoading(true);
      setError(null);
      setApiError(null);
      
      // Get current location first
      const location = await getCurrentLocation();
      setCurrentLocation(location);
      
      const weatherData = await getCurrentWeather(ldClient);
      __DEV__ && console.log('🔍 DEBUG: Weather data received:', weatherData);
      setWeather(weatherData);
      setLastUpdated(new Date());
      
      // Calculate moon phase
      const currentMoonPhase = moonPhaseService.getCurrentMoonPhase();
      setMoonPhase(currentMoonPhase);
    } catch (err) {
      __DEV__ && console.error('🔍 DEBUG: fetchWeather error:', err);
      
      // Handle WeatherAPIError objects
      if (err && typeof err === 'object' && 'type' in err) {
        const weatherError = err as WeatherAPIError;
        __DEV__ && console.log('🔍 DEBUG: WeatherAPIError detected:', weatherError);
        
        // For API key errors, show the modal
        if (weatherError.type === 'API_KEY_INVALID') {
          setApiError(weatherError);
        } else {
          setError(`API Error ${weatherError.code}: ${weatherError.message}`);
        }
        
        // Provide fallback demo data
        const fallbackData = {
          temperature: 22,
          description: 'Demo Weather',
          location: 'Demo City, XX',
          humidity: 65,
          windSpeed: 12,
          icon: '01d',
          provider: 'demo',
          mockData: true,
          hasRain: false,
          hasAlerts: false,
          alerts: []
        };
        setWeather(fallbackData);
        setLastUpdated(new Date());
        
        // Calculate moon phase for demo data
        const demoMoonPhase = moonPhaseService.getCurrentMoonPhase();
        setMoonPhase(demoMoonPhase);
      } else {
        // Handle generic errors
        setError('Failed to fetch weather data');
        console.error('Weather fetch error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch forecast data
  const fetchForecast = async () => {
    __DEV__ && console.log('🔍 DEBUG: fetchForecast called with:', { currentLocation, weather: weather?.hourlyForecast?.length });
    if (!currentLocation || !weather) {
      __DEV__ && console.log('🔍 DEBUG: fetchForecast early return - missing data');
      return;
    }
    
    setForecastLoading(true);
    try {
      // Use the hourlyForecast data that's already available in the weather response
      if (weather.hourlyForecast && weather.hourlyForecast.length > 0) {
        __DEV__ && console.log('🔍 DEBUG: Using hourlyForecast from weather data:', weather.hourlyForecast);
        
        // Transform the hourlyForecast data to match the expected format
        const transformedForecast = weather.hourlyForecast.map((hour: any) => ({
          time: new Date(hour.dt * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          temperature: Math.round(hour.temp),
          description: hour.weather[0]?.description || 'Unknown',
          icon: hour.weather[0]?.icon || '01d',
          humidity: hour.humidity,
          windSpeed: Math.round(hour.wind_speed * 3.6), // Convert m/s to km/h
          pressure: hour.pressure,
          dewPoint: hour.dew_point,
          visibility: hour.visibility,
          windDirection: hour.wind_deg,
          cloudCover: hour.clouds,
          hasRain: hour.weather[0]?.main === 'Rain' || hour.weather[0]?.main === 'Drizzle',
          pop: hour.pop
        }));
        
        __DEV__ && console.log('🔍 DEBUG: Transformed forecast data:', transformedForecast);
        setForecast(transformedForecast);
        __DEV__ && console.log('🔍 DEBUG: Forecast state set to:', transformedForecast.length, 'items');
      } else {
        __DEV__ && console.log('🔍 DEBUG: No hourlyForecast data available in weather response');
        setForecast([]);
        __DEV__ && console.log('🔍 DEBUG: Forecast state set to empty array');
      }
    } catch (error) {
      console.error('Error processing forecast data:', error);
      setForecast([]);
    } finally {
      setForecastLoading(false);
    }
  };

  // Handle weather icon click
  const handleWeatherIconClick = () => {
    __DEV__ && console.log('🔍 DEBUG: Weather icon clicked!');
    __DEV__ && console.log('🔍 DEBUG: Current showForecast state:', showForecast);
    __DEV__ && console.log('🔍 DEBUG: Current weather data:', weather);
    
    if (!showForecast) {
      __DEV__ && console.log('🔍 DEBUG: Fetching forecast...');
      fetchForecast();
    }
    setShowForecast(!showForecast);
    setShowWeatherTooltip(false); // Hide tooltip when showing forecast
    __DEV__ && console.log('🔍 DEBUG: New showForecast state will be:', !showForecast);
  };

  const handleOptionsClick = () => {
    setIsOptionsOpen(true);
    try {
      ldClient?.track('options_modal_opened', {
        current_temperature_unit: temperatureUnit,
        current_theme: theme,
        timestamp: new Date().toISOString()
      });
    } catch {
      // Never let telemetry errors break the interaction
    }
  };

  const handleCloseOptions = () => {
    setIsOptionsOpen(false);
  };

  const handleCloseApiError = () => {
    setApiError(null);
  };

  const handleOpenAirQuality = () => {
    setIsAirQualityOpen(true);
  };

  const handleCloseAirQuality = () => {
    setIsAirQualityOpen(false);
  };

  const handleOpenUVIndex = () => {
    setIsUVIndexOpen(true);
  };

  const handleCloseUVIndex = () => {
    setIsUVIndexOpen(false);
  };

  const handleForecastPageChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentForecastPage > 0) {
      setCurrentForecastPage(currentForecastPage - 1);
    } else if (direction === 'next' && currentForecastPage < totalForecastPages - 1) {
      setCurrentForecastPage(currentForecastPage + 1);
    }
  };

  const toggleForecastMode = async () => {
    const newMode = forecastMode === 'hourly' ? 'daily' : 'hourly';
    setForecastMode(newMode);
    setCurrentForecastPage(0); // Reset to first page when switching modes
    
    // Fetch daily forecast data if switching to daily mode and we don't have it yet
    if (newMode === 'daily' && dailyForecast.length === 0) {
      try {
        setForecastLoading(true);
        const dailyData = await getDailyForecast(ldClient);
        setDailyForecast(dailyData);
      } catch (error) {
        console.error('Failed to fetch daily forecast:', error);
      } finally {
        setForecastLoading(false);
      }
    }
  };

  const toggleAlertExpansion = (alertIndex: number) => {
    setExpandedAlerts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(alertIndex)) {
        newSet.delete(alertIndex);
      } else {
        newSet.add(alertIndex);
      }
      return newSet;
    });
  };

  useEffect(() => {
    __DEV__ && console.log('🔍 DEBUG: WeatherWidget useEffect setting up');
    
    // Add global error handler to catch unhandled errors
    const handleError = (event: ErrorEvent) => {
      __DEV__ && console.error('🔍 DEBUG: Unhandled error detected:', event.error);
      __DEV__ && console.error('🔍 DEBUG: Error details:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      });
    };
    
    window.addEventListener('error', handleError);
    
    fetchWeather();
    
    // Get refresh interval from LaunchDarkly flag (in minutes, default 5 minutes)
    const refreshIntervalMinutes = ldClient ? ldClient.variation('weather-refresh-interval', 5) : 5;
    const refreshIntervalMs = refreshIntervalMinutes * 60 * 1000; // Convert minutes to milliseconds
    
    // Update debug service with current interval
    weatherDebug.updateRefreshInterval(refreshIntervalMinutes);
    
    console.log(`🔍 DEBUG: Setting weather refresh interval to ${refreshIntervalMinutes} minutes (${refreshIntervalMs}ms)`);
    
    // Set up auto-refresh with configurable interval
    const interval = setInterval(fetchWeather, refreshIntervalMs);
    
    // Subscribe to location simulation changes
    const unsubscribeLocationSimulation = locationSimulationService.subscribe((state) => {
      try {
        __DEV__ && console.log('🔍 DEBUG: WeatherWidget received location simulation state change:', state);
        // Refresh weather data when location simulation changes
        fetchWeather();
      } catch (error) {
        __DEV__ && console.error('🔍 DEBUG: Error in WeatherWidget location simulation subscription:', error);
      }
    });
    
    __DEV__ && console.log('🔍 DEBUG: WeatherWidget subscription set up');
    
    return () => {
      __DEV__ && console.log('🔍 DEBUG: WeatherWidget cleanup');
      window.removeEventListener('error', handleError);
      clearInterval(interval);
      // Defensive check to ensure unsubscribe function exists
      if (typeof unsubscribeLocationSimulation === 'function') {
        unsubscribeLocationSimulation();
      } else {
        console.warn('🔍 DEBUG: unsubscribeLocationSimulation is not a function:', typeof unsubscribeLocationSimulation);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchWeather and weatherDebug are intentionally stable for mount/ldClient
  }, [ldClient]);

  // Listen for LaunchDarkly flag changes to trigger re-renders
  useEffect(() => {
    if (!ldClient) return;
    
    const handleFlagChange = (changes: any) => {
      if (changes['enable-animations']) {
        __DEV__ && console.log('🔍 DEBUG: enable-animations flag changed:', changes['enable-animations']);
        // Force re-render by updating a dummy state or just let the component re-render naturally
        // The component will automatically pick up the new flag value on next render
      }
      if (changes['show-extra-weather-info']) {
        __DEV__ && console.log('🔍 DEBUG: show-extra-weather-info flag changed:', changes['show-extra-weather-info']);
        // Component will automatically re-render and pick up the new flag value
      }
      // Only apply the flag if the user hasn't made a manual choice (which is persisted)
      if (changes['default-temperature'] && !localStorage.getItem('temperatureUnit')) {
        __DEV__ && console.log('🔍 DEBUG: default-temperature flag changed:', changes['default-temperature']);
        const newTempUnit = changes['default-temperature'].current;
        // Support 'c', 'f', and 'k' values from LaunchDarkly
        if (newTempUnit === 'c' || newTempUnit === 'f' || newTempUnit === 'k') {
          setTemperatureUnit(newTempUnit);
        } else {
          setTemperatureUnit('c'); // Fallback to Celsius
        }
      }
      if (changes['default-distance'] && !localStorage.getItem('distanceUnit')) {
        __DEV__ && console.log('🔍 DEBUG: default-distance flag changed:', changes['default-distance']);
        setDistanceUnit(changes['default-distance'].current);
      }
      if (changes['show-moon-phase']) {
        __DEV__ && console.log('🔍 DEBUG: show-moon-phase flag changed:', changes['show-moon-phase']);
        // Component will automatically re-render and pick up the new flag value
      }
      if (changes['enable-maximized-layout']) {
        __DEV__ && console.log('🔍 DEBUG: enable-maximized-layout flag changed:', changes['enable-maximized-layout']);
      }
    };
    
    ldClient.on('change', handleFlagChange);
    
    return () => {
      ldClient.off('change', handleFlagChange);
    };
  }, [ldClient]);

  // Initialize temperature unit: a manual choice (localStorage) always wins over the LaunchDarkly flag.
  // The flag is still evaluated unconditionally so LaunchDarkly records an impression event.
  useEffect(() => {
    if (!ldClient) return;
    const flagValue = ldClient.variation('default-temperature', 'c');

    const saved = localStorage.getItem('temperatureUnit');
    if (saved === 'c' || saved === 'f' || saved === 'k') {
      setTemperatureUnit(saved);
      return;
    }
    // Support 'c', 'f', and 'k' values from LaunchDarkly
    if (flagValue === 'c' || flagValue === 'f' || flagValue === 'k') {
      setTemperatureUnit(flagValue);
    } else {
      setTemperatureUnit('c'); // Fallback to Celsius
    }
  }, [ldClient]);

  // Initialize distance unit: a manual choice (localStorage) always wins over the LaunchDarkly flag.
  // The flag is still evaluated unconditionally so LaunchDarkly records an impression event.
  useEffect(() => {
    if (!ldClient) return;
    const flagValue = ldClient.variation('default-distance', 'm');

    const saved = localStorage.getItem('distanceUnit');
    if (saved === 'm' || saved === 'i') {
      setDistanceUnit(saved);
      return;
    }
    setDistanceUnit(flagValue);
  }, [ldClient]);

  // Manual unit changes persist so a flag re-evaluation (e.g. on re-identify) can't clobber them
  const handleTemperatureUnitChange = (unit: 'c' | 'f' | 'k') => {
    try {
      localStorage.setItem('temperatureUnit', unit);
    } catch (error) {
      console.warn('Failed to save temperature unit preference:', error);
    }

    if (unit !== temperatureUnit) {
      try {
        ldClient?.track('temperature_unit_change', {
          from_unit: temperatureUnit,
          to_unit: unit,
          timestamp: new Date().toISOString()
        });
      } catch {
        // Never let telemetry errors break the interaction
      }
    }

    setTemperatureUnit(unit);
  };

  const handleDistanceUnitChange = (unit: 'm' | 'i') => {
    try {
      localStorage.setItem('distanceUnit', unit);
    } catch (error) {
      console.warn('Failed to save distance unit preference:', error);
    }
    setDistanceUnit(unit);
  };

  const getWeatherIcon = (iconCode: string) => {
    // Get the base weather icon
    const baseIconMap: { [key: string]: string } = {
      '01d': '☀️', '01n': '🌙', // Clear night - just moon
      '02d': '⛅', '02n': '☁️',
      '03d': '☁️', '03n': '☁️',
      '04d': '☁️', '04n': '☁️',
      '09d': '🌧️', '09n': '🌧️',
      '10d': '🌦️', '10n': '🌧️',
      '11d': '⛈️', '11n': '⛈️',
      '13d': '❄️', '13n': '❄️',
      '50d': '🌫️', '50n': '🌫️',
    };
    
    return baseIconMap[iconCode] || '🌡️';
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const convertCelsiusToFahrenheit = (celsius: number): number => {
    return Math.round((celsius * 9/5) + 32);
  };

  const convertCelsiusToKelvin = (celsius: number): number => {
    return Math.round((celsius + 273.15) * 100) / 100; // Keep two decimal places for precision
  };

  const formatTemperature = (temperature: number): string => {
    if (temperatureUnit === 'f') {
      return `${convertCelsiusToFahrenheit(temperature)}°F`;
    } else if (temperatureUnit === 'k') {
      return `${convertCelsiusToKelvin(temperature)} K`;
    }
    return `${temperature}°C`;
  };

  const convertKmhToMph = (kmh: number): number => {
    return Math.round(kmh * 0.621371);
  };

  const formatWindSpeed = (windSpeed: number): string => {
    if (distanceUnit === 'i') {
      return `${convertKmhToMph(windSpeed)} MPH`;
    }
    return `${windSpeed} KM/H`;
  };

  const widgetClassName = [
    'weather-widget',
    theme,
    enableAnimations && 'animated',
    showMaximized && 'maximized',
  ].filter(Boolean).join(' ');
  const frameClassName = ['terminal-frame', showMaximized && 'maximized'].filter(Boolean).join(' ');

  const renderTerminalHeader = () => (
    <div className="terminal-header">
      <span className="terminal-title">WEATHER.SYS</span>
      <div className="terminal-controls">
        <span className="control-dot red" aria-hidden="true" />
        <button
          type="button"
          className={`control-dot yellow${enableMaximizedLayout ? ' interactive' : ''}`}
          onClick={handleRestore}
          disabled={!enableMaximizedLayout || !showMaximized}
          aria-label="Restore window"
          title={enableMaximizedLayout ? 'Restore' : undefined}
        />
        <button
          type="button"
          className={`control-dot green${enableMaximizedLayout ? ' interactive' : ''}`}
          onClick={handleMaximize}
          disabled={!enableMaximizedLayout || showMaximized}
          aria-label="Maximize window"
          title={enableMaximizedLayout ? 'Maximize' : undefined}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={widgetClassName} role="main">
        <div className={frameClassName}>
          {renderTerminalHeader()}
          <div className="terminal-content">
            <div className="loading-animation">
              {enableAnimations && <div className="scan-line"></div>}
              <pre className="ascii-art">
{`
█░█░█ █▀▀ ▄▀█ ▀█▀ █░█ █▀▀ █▀█
▀▄▀▄▀ ██▄ █▀█ ░█░ █▀█ ██▄ █▀▄

     L O A D I N G . . .
`}
              </pre>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={widgetClassName} role="main">
        <div className={frameClassName}>
          {renderTerminalHeader()}
          <div className="terminal-content">
            <div className="error-message">
              <pre>
{`
█▀▀ █▀█ █▀█ █▀█ █▀█
██▄ █▀▄ █▀▄ █▄█ █▀▄

SYSTEM ERROR: ${error}
`}
              </pre>
              <div className="button-group">
                <button className="refresh-button" onClick={handleOptionsClick}>
                  [ OPTIONS ]
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!weather) return null;

  return (
    <div className={widgetClassName} role="main">
      <div className={frameClassName}>
        {renderTerminalHeader()}
        <div className="terminal-content">
          <div className="weather-display">
            <div className="weather-header">
              {!showForecast ? (
                // Current weather display
                <>
                  <div 
                    className={weather.icon.endsWith('n') ? "weather-icon weather-icon-night" : "weather-icon"}
                    title={weather.description}
                    onMouseEnter={() => setShowWeatherTooltip(true)}
                    onMouseLeave={() => setShowWeatherTooltip(false)}
                    onClick={handleWeatherIconClick}
                    style={{ cursor: 'pointer' }}
                  >
                    {weather.icon.endsWith('n') && moonPhase && weather.icon !== '01n' ? (
                      <>
                        <span className="moon-phase">{moonPhase.icon}</span>
                        <span className="weather-overlay">{getWeatherIcon(weather.icon)}</span>
                      </>
                    ) : (
                      getWeatherIcon(weather.icon)
                    )}
                    {/* Rain indicator - only show for OpenWeather providers */}
                    {(weather.provider === 'openweathermap' || weather.provider === 'openweathermap-onecall') && weather.hasRain && (
                      <div className="weather-indicator rain-indicator" title="Rain expected">
                        💧
                      </div>
                    )}
                    {/* Alert indicator - only show for OpenWeather providers */}
                    {(weather.provider === 'openweathermap' || weather.provider === 'openweathermap-onecall') && weather.hasAlerts && (
                      <div className="weather-indicator alert-indicator" title="Weather alert active">
                        ⚠️
                      </div>
                    )}
                  </div>
                  {showWeatherTooltip && (
                    <div className="weather-tooltip">
                      <div className="weather-tooltip-content">
                        <strong>{weather.description}</strong>
                        <div className="weather-tooltip-details">
                          <small>Temperature: {formatTemperature(weather.temperature)}</small>
                          <small>Humidity: {weather.humidity}%</small>
                          <small>Wind: {formatWindSpeed(weather.windSpeed)}</small>
                          {/* Show rain probability if available */}
                          {(weather.provider === 'openweathermap' || weather.provider === 'openweathermap-onecall') && weather.hasRain && (
                            <small>🌧️ Rain expected</small>
                          )}
                          {/* Show alert info if available */}
                          {(weather.provider === 'openweathermap' || weather.provider === 'openweathermap-onecall') && weather.hasAlerts && weather.alerts && weather.alerts.length > 0 && (
                            <small>⚠️ {weather.alerts[0].event}</small>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="weather-temp">{formatTemperature(weather.temperature)}</div>
                </>
              ) : (
                // Forecast display
                <>
                  <div className="forecast-container">
                    <div className="forecast-header">
                      <div className="forecast-header-left">
                        <span className="forecast-title">
                          {forecastMode === 'hourly' ? '12-Hour Forecast' : '7-Day Outlook'}
                        </span>
                        <button 
                          className="forecast-mode-toggle"
                          onClick={toggleForecastMode}
                          title={`Switch to ${forecastMode === 'hourly' ? '7-Day' : '12-Hour'} view`}
                        >
                          {forecastMode === 'hourly' ? '📅' : '⏰'}
                        </button>
                      </div>
                      <div className="forecast-header-right">
                        <button 
                          className="forecast-close"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowForecast(false);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                    {/* Forecast Navigation - Show on all devices when there are multiple pages */}
                    {totalForecastPages > 1 && (
                      <div className="forecast-navigation">
                        <button 
                          className="forecast-nav-btn prev"
                          onClick={() => handleForecastPageChange('prev')}
                          disabled={currentForecastPage === 0}
                        >
                          ◀
                        </button>
                        <span className="forecast-page-indicator">
                          {currentForecastPage + 1} / {totalForecastPages}
                        </span>
                        <button 
                          className="forecast-nav-btn next"
                          onClick={() => handleForecastPageChange('next')}
                          disabled={currentForecastPage === totalForecastPages - 1}
                        >
                          ▶
                        </button>
                      </div>
                    )}
                    
                    <div className="forecast-grid">
                      {forecastLoading ? (
                        <div className="forecast-loading">Loading forecast...</div>
                      ) : forecastMode === 'hourly' && forecast.length > 0 ? (
                        getForecastPage(forecast, currentForecastPage).map((hour, index) => (
                          <div 
                            key={index} 
                            className="forecast-square"
                          >
                            <div className="forecast-time">{hour.time}</div>
                            <div className={hour.icon.endsWith('n') ? "forecast-icon weather-icon-night" : "forecast-icon"}>
                              {hour.icon.endsWith('n') && moonPhase && hour.icon !== '01n' ? (
                                <>
                                  <span className="moon-phase">{moonPhase.icon}</span>
                                  <span className="weather-overlay">{getWeatherIcon(hour.icon)}</span>
                                </>
                              ) : (
                                getWeatherIcon(hour.icon)
                              )}
                              {/* Rain indicator for forecast hours */}
                              {hour.hasRain && (
                                <div className="forecast-rain-indicator" title={`${Math.round((hour.pop || 0) * 100)}% chance of rain`}>
                                  💧
                                </div>
                              )}
                            </div>
                            <div className="forecast-temp">{formatTemperature(hour.temperature)}</div>
                            
                            {/* Enhanced weather details */}
                            <div className="forecast-details">
                              {hour.pressure && (
                                <div className="forecast-detail-item">
                                  <span className="detail-icon">🌡️</span>
                                  <span className="detail-value">{hour.pressure} hPa</span>
                                </div>
                              )}
                              {hour.dewPoint && (
                                <div className="forecast-detail-item">
                                  <span className="detail-icon">💧</span>
                                  <span className="detail-value">{Math.round(hour.dewPoint)}°</span>
                                </div>
                              )}
                              {hour.visibility && (
                                <div className="forecast-detail-item">
                                  <span className="detail-icon">👁️</span>
                                  <span className="detail-value">{(hour.visibility / 1000).toFixed(1)} km</span>
                                </div>
                              )}
                              {hour.windDirection && (
                                <div className="forecast-detail-item">
                                  <span className="detail-icon">🧭</span>
                                  <span className="detail-value">{hour.windDirection}°</span>
                                </div>
                              )}
                              {hour.cloudCover && (
                                <div className="forecast-detail-item">
                                  <span className="detail-icon">☁️</span>
                                  <span className="detail-value">{hour.cloudCover}%</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Forecast tooltip - temporarily disabled to fix display issues */}
                            {/* {hoveredForecastIndex === index && (
                              <div className="forecast-tooltip">
                                <div className="forecast-tooltip-content">
                                  <div className="forecast-tooltip-time">{hour.time}</div>
                                  <div className="forecast-tooltip-description">{hour.description}</div>
                                  <div className="forecast-tooltip-details">
                                    <div className="forecast-tooltip-detail">
                                      <span className="detail-label">Temperature:</span>
                                      <span className="detail-value">{formatTemperature(hour.temperature)}</span>
                                    </div>
                                    <div className="forecast-tooltip-detail">
                                      <span className="detail-label">Humidity:</span>
                                      <span className="detail-value">{hour.humidity}%</span>
                                    </div>
                                    <div className="forecast-tooltip-detail">
                                      <span className="detail-label">Wind:</span>
                                      <span className="detail-value">{formatWindSpeed(hour.windSpeed)}</span>
                                    </div>
                                    {hour.hasRain && hour.pop && (
                                      <div className="forecast-tooltip-detail">
                                        <span className="detail-label">Rain Chance:</span>
                                        <span className="detail-value">{Math.round(hour.pop * 100)}%</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )} */}
                          </div>
                        ))
                      ) : forecastMode === 'daily' && dailyForecast.length > 0 ? (
                        getDailyForecastPage(dailyForecast, currentForecastPage).map((day, index) => (
                          <div key={index} className="forecast-square daily-forecast">
                            <div className="forecast-date">{day.date}</div>
                            <div className="forecast-icon">
                              {getWeatherIcon(day.icon)}
                              {day.hasRain && (
                                <div className="forecast-rain-indicator" title={`${Math.round((day.pop || 0) * 100)}% chance of rain`}>
                                  💧
                                </div>
                              )}
                            </div>
                            <div className="forecast-temp-range">
                              <span className="temp-max">{day.tempMax}°</span>
                              <span className="temp-min">{day.tempMin}°</span>
                            </div>
                            
                            {/* Enhanced weather details for daily */}
                            <div className="forecast-details">
                              {day.pressure && (
                                <div className="forecast-detail-item">
                                  <span className="detail-icon">🌡️</span>
                                  <span className="detail-value">{day.pressure} hPa</span>
                                </div>
                              )}
                              {day.humidity && (
                                <div className="forecast-detail-item">
                                  <span className="detail-icon">💧</span>
                                  <span className="detail-value">{day.humidity}%</span>
                                </div>
                              )}
                              {day.windSpeed && (
                                <div className="forecast-detail-item">
                                  <span className="detail-icon">💨</span>
                                  <span className="detail-value">{day.windSpeed} km/h</span>
                                </div>
                              )}
                              {day.sunrise && day.sunset && (
                                <div className="forecast-detail-item">
                                  <span className="detail-icon">🌅</span>
                                  <span className="detail-value">{day.sunrise} / {day.sunset}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="forecast-error">No forecast data available</div>
                      )}
                    </div>
                    
                    {/* Weather Alerts Section */}
                    {(weather.provider === 'openweathermap' || weather.provider === 'openweathermap-onecall') && 
                     weather.hasAlerts && 
                     weather.alerts && 
                     weather.alerts.length > 0 && (
                      <div className="forecast-alerts">
                        <div className="forecast-alerts-header">
                          <span className="forecast-alerts-title">⚠️ WEATHER ALERTS</span>
                        </div>
                        <div className="forecast-alerts-content">
                          {weather.alerts.map((alert: any, alertIndex: number) => (
                            <div key={alertIndex} className="forecast-alert-item">
                              <div 
                                className="forecast-alert-header"
                                onClick={() => toggleAlertExpansion(alertIndex)}
                              >
                                <div className="forecast-alert-event">{alert.event}</div>
                                {alert.start && alert.end && (
                                  <div className="forecast-alert-time">
                                    <span className="alert-time-label">Active:</span>
                                    <span className="alert-time-value">
                                      {new Date(alert.start * 1000).toLocaleDateString()} {new Date(alert.start * 1000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {new Date(alert.end * 1000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    </span>
                                  </div>
                                )}
                                <div className="forecast-alert-toggle">
                                  {expandedAlerts.has(alertIndex) ? '▼' : '▶'}
                                </div>
                              </div>
                              {expandedAlerts.has(alertIndex) && (
                                <div className="forecast-alert-description">
                                  {alert.description}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            
            <div className="weather-info">
              <div className="location">{weather.location}</div>
              <div className="description">{weather.description.toUpperCase()}</div>
            </div>

            {showExtraWeatherInfo && (
              <div className="weather-details">
                <div className="detail-row">
                  <span className="detail-label">HUMIDITY:</span>
                  <span className="detail-value">{weather.humidity}%</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">WIND:</span>
                  <span className="detail-value">{formatWindSpeed(weather.windSpeed)}</span>
                </div>
                
                {/* Enhanced Weather Data Buttons */}
                {weather?.airQuality && (
                  <div className="detail-row enhanced-data">
                    <span className="detail-label">AIR QUALITY:</span>
                    <button 
                      className="enhanced-data-button air-quality-button"
                      onClick={handleOpenAirQuality}
                      title={`${weather.airQuality.category} (${weather.airQuality.index})`}
                    >
                      {weather.airQuality.index} - {weather.airQuality.category}
                    </button>
                  </div>
                )}
                
                {weather?.uvIndex && (
                  <div className="detail-row enhanced-data">
                    <span className="detail-label">UV INDEX:</span>
                    <button 
                      className="enhanced-data-button uv-index-button"
                      onClick={handleOpenUVIndex}
                      title={`${weather.uvIndex.risk} risk`}
                    >
                      {weather.uvIndex.value} - {weather.uvIndex.risk}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Moon Phase Display */}
            {moonPhase && getShowMoonPhase() && (
              <div className="moon-phase-section">
                <div className="moon-phase-icon" title={moonPhase.description}>
                  {moonPhase.icon}
                </div>
                <div className="moon-phase-detail">
                  <span className="moon-phase-label">MOON:</span>
                  <span className="moon-phase-value">{moonPhase.description}</span>
                </div>
              </div>
            )}

            <div className="terminal-footer">
              <div className="timestamp">
                LAST UPDATE: {lastUpdated && formatTime(lastUpdated)}
              </div>
              <div className="button-group">
                <button className="refresh-button" onClick={handleOptionsClick}>
                  [ OPTIONS ]
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {enableAnimations && (
          <div className="crt-effects">
            <div className="scan-lines"></div>
            <div className="screen-flicker"></div>
          </div>
        )}
      </div>
      
      <OptionsModal 
        isOpen={isOptionsOpen} 
        onClose={handleCloseOptions} 
        temperatureUnit={temperatureUnit}
        onTemperatureUnitChange={handleTemperatureUnitChange}
        distanceUnit={distanceUnit}
        onDistanceUnitChange={handleDistanceUnitChange}
      />
      
      <APIErrorModal
        isOpen={apiError !== null}
        onClose={handleCloseApiError}
        errorCode={apiError?.code}
        errorMessage={apiError?.message}
      />
      
      {/* Enhanced Weather Data Modals */}
      {weather?.airQuality && (
        <AirQualityModal
          isOpen={isAirQualityOpen}
          onClose={handleCloseAirQuality}
          airQuality={weather.airQuality}
        />
      )}
      
      {weather?.uvIndex && (
        <UVIndexModal
          isOpen={isUVIndexOpen}
          onClose={handleCloseUVIndex}
          uvIndex={weather.uvIndex}
        />
      )}
    </div>
  );
};

export default WeatherWidget; 